import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { trackLead } from "@/utils/metaPixel";
import { 
  Handshake, 
  Heart, 
  Instagram, 
  CheckCircle2,
  Users,
  Eye,
  Megaphone,
  ArrowRight,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SponsorPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company_name: "",
    phone: "",
    website: "",
    budget_range: "",
    interest: "",
    consent: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Bitte gib deinen Namen ein");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein");
      return;
    }
    if (!formData.company_name.trim()) {
      toast.error("Bitte gib deinen Firmennamen ein");
      return;
    }
    if (!formData.budget_range) {
      toast.error("Bitte wähle ein Budget");
      return;
    }
    if (!formData.consent) {
      toast.error("Bitte stimme der Datenschutzerklärung zu");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/sponsor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Fire STANDARD Lead event - NO PARAMETERS
        trackLead();
        navigate("/sponsor-danke");
      } else {
        toast.error(data.detail || data.message || "Ein Fehler ist aufgetreten");
      }
    } catch (error) {
      console.error("Sponsor application error:", error);
      toast.error("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("sponsor-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-body">
      {/* Hero Section */}
      <section className="relative px-4 py-12 md:py-20">
        <div className="max-w-4xl mx-auto">
          {/* Brand Tag */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B2332]/10 text-[#8B2332] text-sm font-medium mb-6">
            <Handshake className="w-4 h-4" />
            <span>Für Sponsoren & Partner</span>
          </div>

          {/* Headline */}
          <h1 
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-tight mb-4"
            data-testid="sponsor-headline"
          >
            Sponsor des{" "}
            <span className="text-[#8B2332] italic">Hijab & Halal</span>{" "}
            Pop-ups werden
          </h1>

          <p className="text-lg md:text-xl text-[#6B6B6B] mb-8 max-w-2xl">
            Erreiche über 1.000 muslimische Frauen in Hamburg. 
            Verschiedene Sponsoring-Pakete von 100 € bis 5.000 €.
          </p>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-[#EFEBE5]">
              <Users className="w-6 h-6 text-[#8B2332] mb-2" />
              <div className="font-heading text-2xl text-[#2D2D2D]">~1.200</div>
              <p className="text-[#6B6B6B] text-sm">Besucherinnen</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#EFEBE5]">
              <Eye className="w-6 h-6 text-[#8B2332] mb-2" />
              <div className="font-heading text-2xl text-[#2D2D2D]">50k+</div>
              <p className="text-[#6B6B6B] text-sm">Social Reach</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#EFEBE5]">
              <Megaphone className="w-6 h-6 text-[#8B2332] mb-2" />
              <div className="font-heading text-2xl text-[#2D2D2D]">8h</div>
              <p className="text-[#6B6B6B] text-sm">Präsenz</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#EFEBE5]">
              <Handshake className="w-6 h-6 text-[#8B2332] mb-2" />
              <div className="font-heading text-2xl text-[#2D2D2D]">ab 100€</div>
              <p className="text-[#6B6B6B] text-sm">Sponsoring</p>
            </div>
          </div>

          <Button
            onClick={scrollToForm}
            className="bg-[#8B2332] hover:bg-[#7A1E2B] text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:-translate-y-1 hover:shadow-xl"
            data-testid="sponsor-cta-hero"
          >
            Jetzt anfragen
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl text-[#2D2D2D] text-center mb-8">
            Sponsoring-Pakete
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Bronze */}
            <div className="bg-[#FAF7F2] rounded-2xl p-6 border border-[#EFEBE5]">
              <div className="font-heading text-xl text-[#2D2D2D] mb-2">Bronze</div>
              <div className="font-heading text-3xl text-[#8B2332] mb-4">100 – 499 €</div>
              <ul className="space-y-2 text-[#6B6B6B] text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Logo auf Event-Material</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Erwähnung in Social Media</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Goodie-Bag Beilage</span>
                </li>
              </ul>
            </div>

            {/* Silver */}
            <div className="bg-[#8B2332]/5 rounded-2xl p-6 border-2 border-[#8B2332]/20 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8B2332] text-white text-xs px-3 py-1 rounded-full">
                Beliebt
              </div>
              <div className="font-heading text-xl text-[#2D2D2D] mb-2">Silber</div>
              <div className="font-heading text-3xl text-[#8B2332] mb-4">500 – 1.999 €</div>
              <ul className="space-y-2 text-[#6B6B6B] text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Alles aus Bronze</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Banner am Event</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Story-Feature</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Promo-Stand möglich</span>
                </li>
              </ul>
            </div>

            {/* Gold */}
            <div className="bg-[#FAF7F2] rounded-2xl p-6 border border-[#EFEBE5]">
              <div className="font-heading text-xl text-[#2D2D2D] mb-2">Gold</div>
              <div className="font-heading text-3xl text-[#8B2332] mb-4">2.000 – 5.000 €</div>
              <ul className="space-y-2 text-[#6B6B6B] text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Alles aus Silber</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Haupt-Sponsor Status</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Bühnen-Präsenz</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#8B2332] mt-0.5 flex-shrink-0" />
                  <span>Exklusive Kategorie</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Past Sponsors Section */}
      <section className="py-12 px-4 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl text-[#2D2D2D] text-center mb-8">
            Bisherige Sponsoren & Partner
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: "BALCOK®", tagline: "Alles beginnt mit Dir" },
              { name: "HalalBooking", tagline: "Halal Travel" },
              { name: "Josef's Bio", tagline: "Bio Food" },
              { name: "modanisa", tagline: "Modest Fashion" },
              { name: "INIA", tagline: "Beauty" },
              { name: "KT BANK", tagline: "Islamic Banking" },
            ].map((sponsor) => (
              <div 
                key={sponsor.name}
                className="bg-white rounded-xl p-4 border border-[#EFEBE5] text-center hover:shadow-md transition-shadow"
              >
                <div className="font-heading text-lg text-[#2D2D2D] mb-1">
                  {sponsor.name}
                </div>
                <p className="text-[#6B6B6B] text-xs">{sponsor.tagline}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-[#6B6B6B] text-sm mt-6">
            + weitere Partner wie Kleding.nl
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#8B2332]/5 border border-[#8B2332]/20 rounded-2xl p-6 md:p-8">
            <h2 className="font-heading text-2xl text-[#2D2D2D] mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#8B2332]" />
              Warum sponsern?
            </h2>
            <ul className="space-y-3 text-[#6B6B6B]">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8B2332] mt-0.5 flex-shrink-0" />
                <span>Direkter Zugang zu einer loyalen, kaufkräftigen Zielgruppe</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8B2332] mt-0.5 flex-shrink-0" />
                <span>Authentische Markenpräsenz in der muslimischen Community</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8B2332] mt-0.5 flex-shrink-0" />
                <span>Social Media Coverage durch EDYT & BLOOM Kanäle</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8B2332] mt-0.5 flex-shrink-0" />
                <span>Networking mit anderen Brands und Entscheidern</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 px-4 bg-[#FAF7F2]" id="sponsor-form">
        <div className="max-w-xl mx-auto">
          <h2 className="font-heading text-3xl text-[#2D2D2D] text-center mb-8">
            Sponsoring anfragen
          </h2>

          <div 
            className="bg-[#FAF7F2] rounded-2xl p-6 md:p-8 shadow-lg border border-[#EFEBE5]"
            data-testid="sponsor-form-container"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Dein Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="Vollständiger Name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="h-12 bg-white border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="sponsor-input-name"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    E-Mail *
                  </label>
                  <Input
                    type="email"
                    placeholder="deine@email.de"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="h-12 bg-white border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="sponsor-input-email"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Company Info */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                  Firma / Organisation *
                </label>
                <Input
                  type="text"
                  placeholder="Name deiner Firma"
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  className="h-12 bg-white border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                  data-testid="sponsor-input-company"
                  disabled={isLoading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Telefon
                  </label>
                  <Input
                    type="tel"
                    placeholder="+49..."
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="h-12 bg-white border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="sponsor-input-phone"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Website
                  </label>
                  <Input
                    type="url"
                    placeholder="https://..."
                    value={formData.website}
                    onChange={(e) => handleChange("website", e.target.value)}
                    className="h-12 bg-white border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="sponsor-input-website"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                  Budget-Rahmen *
                </label>
                <Select 
                  value={formData.budget_range} 
                  onValueChange={(value) => handleChange("budget_range", value)}
                  disabled={isLoading}
                >
                  <SelectTrigger 
                    className="h-12 bg-white border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="sponsor-select-budget"
                  >
                    <SelectValue placeholder="Wähle einen Budget-Rahmen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="100-499">Bronze: 100 – 499 €</SelectItem>
                    <SelectItem value="500-1999">Silber: 500 – 1.999 €</SelectItem>
                    <SelectItem value="2000-5000">Gold: 2.000 – 5.000 €</SelectItem>
                    <SelectItem value="custom">Individuelles Paket</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                  Was interessiert dich besonders?
                </label>
                <textarea
                  placeholder="Erzähl uns, was du dir vorstellst..."
                  value={formData.interest}
                  onChange={(e) => handleChange("interest", e.target.value)}
                  className="w-full min-h-[100px] px-4 py-3 bg-white border border-[#DCD6CF] rounded-lg focus:border-[#8B2332] focus:ring-1 focus:ring-[#8B2332] focus:outline-none resize-none"
                  data-testid="sponsor-input-interest"
                  disabled={isLoading}
                />
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => handleChange("consent", checked)}
                  className="mt-1 border-[#8B2332] data-[state=checked]:bg-[#8B2332] data-[state=checked]:border-[#8B2332]"
                  data-testid="sponsor-checkbox-consent"
                  disabled={isLoading}
                />
                <label htmlFor="consent" className="text-sm text-[#6B6B6B] leading-relaxed cursor-pointer">
                  Ich stimme der <Link to="/datenschutz" className="text-[#8B2332] hover:underline">Datenschutzerklärung</Link> zu und möchte Infos zum Event erhalten.
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-[#8B2332] hover:bg-[#7A1E2B] text-white text-lg font-medium rounded-full transition-all hover:-translate-y-1 hover:shadow-xl"
                data-testid="sponsor-submit-button"
                disabled={isLoading}
              >
                {isLoading ? "Wird gesendet..." : "Anfrage absenden"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-[#EFEBE5]">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-[#6B6B6B]">
              <Heart className="w-4 h-4 text-[#8B2332]" />
              <span className="font-medium text-[#2D2D2D]">EDYT & BLOOM</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <Link to="/pass" className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors">
                Besucher-Pass
              </Link>
              <Link to="/vendor" className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors">
                Aussteller
              </Link>
              <Link to="/impressum" className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz" className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors">
                Datenschutz
              </Link>
              <a 
                href="https://www.instagram.com/edytandbloom/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SponsorPage;
